import React, { useId, useRef, useState, useEffect } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { FieldProps } from 'formik';
import 'formik';
import moment from 'moment';
import {
  focusNextInput,
  getInputClasses,
  getValidationStatus,
} from 'Components/frameworks/formik/inputsUtils';
import { ErrorNote } from './ErrorNote';
type Props = FieldProps & {
  className?: string;
  label: string;
  isRequired: boolean;
  isDisabled: boolean;
  monthPlaceholder: string;
  yearPlaceholder: string;
  dateFormat: string;
  value: number | null | undefined;
};
export const CreditCardExpiryInput = ({
  className,
  label,
  field,
  isRequired,
  isDisabled,
  form,
  ...props
}: Props) => {
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(field, form);
  const reactId = useId();
  const { current: uniqIdentifier } = useRef(`dateInput-${reactId}`);
  const refFirstInput = useRef(null);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const onChangeMonth = ({ target }) => {
    setMonth(target.value);
    focusNextInput(target);
  };

  const onChangeYear = ({ target }) => {
    setYear(target.value);
    focusNextInput(target);
  };

  const structure = [
    {
      keyPos: 'm',
      name: `${field.name}-CreditCardExpiryInput-month`,
      value: month,
      length: 2,
      handleChange: onChangeMonth,
      placeholder: props.monthPlaceholder,
    },
    {
      keyPos: 'y',
      name: `${field.name}-CreditCardExpiryInput-year`,
      value: year,
      length: 2,
      handleChange: onChangeYear,
      placeholder: props.yearPlaceholder,
    },
  ].sort(
    // sort each input to respect the locale format (MM/YYYY or YYYY/MM ...)
    (i1, i2) =>
      props.dateFormat.toLowerCase().indexOf(i1.keyPos) -
      props.dateFormat.toLowerCase().indexOf(i2.keyPos),
  );
  useEffect(() => {
    // if no value is entered, no need to set fields to null
    if (!month && !year) {
      return;
    }

    // when the date is incomplete erase the stored birth date to null to not store an invalid date
    if (!structure.every(({ value, length }) => value.length === length)) {
      /**
       * setFieldValue returns a promise even if the doc says the opposite
       * https://github.com/jaredpalmer/formik/blob/f117c04738ed218b5eb8916d7189e0849962d50d/packages/formik/src/Formik.tsx#L602
       * BUT useEffect does not allow a promise as return...
       */
      form.setFieldValue(field.name, null);
      return;
    }

    const expiryDate = moment([+year, +month], 'YY-MM');
    form.setFieldValue(field.name, expiryDate.valueOf());
  }, [month, year]);

  const handleBlur = ({ relatedTarget }) => {
    if (
      !relatedTarget ||
      !relatedTarget.dataset.dateinputidentifier ||
      !relatedTarget.dataset.dateinputidentifier.includes('uniqIdentifier')
    ) {
      form.setFieldTouched(field.name);
    }
  };

  const handleFocus = ({ target }) => {
    if (
      structure.every(({ value }) => value === '') &&
      refFirstInput.current &&
      target !== refFirstInput.current
    ) {
      refFirstInput.current.focus();
    }
  };

  const classes = getInputClasses({
    hasHeight: true,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });
  return (
    <React.Fragment>
      <div className={classnames(classes, 'relative isolate flex', className)}>
        {structure.map(({ name, handleChange, value, length, placeholder }, key) => (
          <input
            autoComplete="off"
            className="outline-none"
            ref={key === 0 ? refFirstInput : undefined}
            key={name}
            name={name}
            type="tel"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            maxLength={length}
            value={value}
            placeholder={placeholder}
            disabled={isDisabled}
            data-dateinputidentifier={uniqIdentifier}
          />
        ))}
        <span
          className={classnames('pointer-events-none absolute left-1/2 -mx-20 flex items-center', {
            'text-success': isValidated,
            'text-red': form.touched[field.name] && isErrored,
          })}
        >
          /
        </span>
      </div>
      <ErrorNote className="mx-15" isErrored={isErrored} errorMessage={form.errors[field.name]} />
    </React.Fragment>
  );
};
